import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import { ApiError, api } from '../lib/api';
import type {
  AdminSettings,
  AiStatus,
  CannedResponse,
  EmailStatus,
  Faq,
  KnowledgeSource,
} from '../types';
import { MaterialSymbol, WidgetGlyph } from '../components/icons';
import FaqModal from '../components/FaqModal';
import { WhatsAppCard } from '../components/WhatsAppCard';
import { relativeFromNow } from '../lib/format';
import { fileToAvatarDataUrl } from '../lib/image';
import { useStore } from '../lib/useStore';

/**
 * Settings — General, AI Chatbot, and FAQs tabs (from the "Updated
 * Settings", "Refined AI Chatbot Settings", and "Refined FAQs Settings"
 * designs). General + AI Chatbot edit one persisted settings object behind
 * shared Discard/Save; the FAQ knowledge base saves immediately through the
 * add/edit modal. Storefront tab and Email/WhatsApp integrations remain
 * "coming soon"; the AI reply engine ships separately.
 */

type Tab = 'General' | 'AI Chatbot' | 'FAQs' | 'Storefront';
const TABS: Tab[] = ['General', 'AI Chatbot', 'FAQs', 'Storefront'];
const REMINDER_CHOICES: Array<AdminSettings['reminderMinutes']> = [5, 10, 30];
const AUTO_CLOSE_CHOICES: Array<AdminSettings['autoCloseHours']> = [1, 2, 6, 12, 24];
const FAQ_PAGE_SIZE = 5;
const LAUNCHER_ICON_CHOICES: Array<AdminSettings['widgetLauncherIcon']> = [
  'chat',
  'bolt',
  'help',
  'message',
];
const SEND_ICON_CHOICES: Array<AdminSettings['widgetSendIcon']> = ['send', 'arrow'];

function toast(message: string, isError = false): void {
  window.shopify?.toast.show(message, isError ? { isError: true } : undefined);
}

function Toggle({
  on,
  label,
  onClick,
}: {
  on: boolean;
  label: string;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={`cg-toggle${on ? ' is-on' : ''}`}
      onClick={onClick}
    />
  );
}

function CategoryChip({ faq }: { faq: Faq }): ReactNode {
  return (
    <span
      className="cg-faq-cat-chip"
      style={{ background: faq.categoryBgColor, color: faq.categoryTextColor }}
    >
      {faq.category}
    </span>
  );
}

/**
 * Dims + blocks a premium panel on the FREE plan, overlaying a lock and an
 * upgrade CTA. Saved configuration underneath is kept and restored on
 * re-upgrade — the server independently ignores it while the store is FREE.
 */
function ProLocked({
  locked,
  onUpgrade,
  children,
}: {
  locked: boolean;
  onUpgrade: () => void;
  children: ReactNode;
}): ReactNode {
  if (!locked) return children;
  return (
    <div className="cg-pro-lock">
      <div className="cg-pro-lock-content" aria-hidden>
        {children}
      </div>
      <div className="cg-pro-lock-cover">
        <MaterialSymbol name="lock" size={22} />
        <span>PRO feature</span>
        <button type="button" className="cg-btn-pill" onClick={onUpgrade}>
          Upgrade to PRO
        </button>
      </div>
    </div>
  );
}

/** Modal state: closed | creating | editing a specific entry. */
type ModalState = { open: false } | { open: true; faq: Faq | null };

export default function SettingsPage(): ReactNode {
  const [tab, setTab] = useState<Tab>('General');
  const [saved, setSaved] = useState<AdminSettings | null>(null);
  const [draft, setDraft] = useState<AdminSettings | null>(null);
  const [aiStatus, setAiStatus] = useState<AiStatus | null>(null);
  const [emailStatus, setEmailStatus] = useState<EmailStatus | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [canned, setCanned] = useState<CannedResponse[]>([]);
  const [cannedDraft, setCannedDraft] = useState<{ id: string | null; title: string; content: string }>({
    id: null,
    title: '',
    content: '',
  });
  const [cannedBusy, setCannedBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [sources, setSources] = useState<KnowledgeSource[]>([]);
  const [sourceUrl, setSourceUrl] = useState('');
  /** 'add' while linking a new page, or the id of a source being worked on. */
  const [sourceBusy, setSourceBusy] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ open: false });
  const [faqSearch, setFaqSearch] = useState('');
  const [faqCategory, setFaqCategory] = useState<string>('All');
  const [faqPage, setFaqPage] = useState(1);
  const { store, refetch: refetchStore } = useStore();
  const [billingBusy, setBillingBusy] = useState(false);
  const [confirmDowngrade, setConfirmDowngrade] = useState(false);

  // Premium surfaces lock on FREE. While the store is still loading we don't
  // lock anything — panels flash locked→unlocked otherwise.
  const isFree = store?.plan === 'FREE';

  useEffect(() => {
    let cancelled = false;
    api
      .getSettings()
      .then(({ settings, ai, email }) => {
        if (cancelled) return;
        setSaved(settings);
        setDraft(settings);
        setAiStatus(ai);
        setEmailStatus(email);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load settings');
      });
    api
      .listFaqs()
      .then(({ faqs: list }) => {
        if (!cancelled) setFaqs(list);
      })
      .catch(() => {
        // Knowledge base list is non-fatal; the panels just start empty.
      });
    api
      .listKnowledgeSources()
      .then(({ sources: list }) => {
        if (!cancelled) setSources(list);
      })
      .catch(() => {
        // Linked pages are non-fatal too.
      });
    api
      .listCannedResponses()
      .then(({ cannedResponses }) => {
        if (!cancelled) setCanned(cannedResponses);
      })
      .catch(() => {
        // Saved replies are non-fatal too.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty = saved !== null && draft !== null && JSON.stringify(saved) !== JSON.stringify(draft);

  const patch = (partial: Partial<AdminSettings>): void => {
    setDraft((d) => (d ? { ...d, ...partial } : d));
  };

  const agentAvatarInput = useRef<HTMLInputElement>(null);
  const launcherImageInput = useRef<HTMLInputElement>(null);
  const sendImageInput = useRef<HTMLInputElement>(null);

  const onAddSource = async (): Promise<void> => {
    const url = sourceUrl.trim();
    if (!url || sourceBusy) return;
    setSourceBusy('add');
    try {
      const { source } = await api.createKnowledgeSource(url);
      setSources((list) => [...list, source]);
      setSourceUrl('');
      if (source.status === 'OK') toast('Page linked — the AI can now answer from it');
      else toast(`Page linked but fetch failed: ${source.error}`, true);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not link that page', true);
    } finally {
      setSourceBusy(null);
    }
  };

  const onRefreshSource = async (sourceId: string): Promise<void> => {
    if (sourceBusy) return;
    setSourceBusy(sourceId);
    try {
      const { source } = await api.refreshKnowledgeSource(sourceId);
      setSources((list) => list.map((s) => (s.id === sourceId ? source : s)));
      if (source.status === 'OK') toast('Page refreshed');
      else toast(`Refresh failed: ${source.error}`, true);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Refresh failed', true);
    } finally {
      setSourceBusy(null);
    }
  };

  const onDeleteSource = async (sourceId: string): Promise<void> => {
    if (sourceBusy) return;
    setSourceBusy(sourceId);
    try {
      await api.deleteKnowledgeSource(sourceId);
      setSources((list) => list.filter((s) => s.id !== sourceId));
      toast('Page removed');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not remove the page', true);
    } finally {
      setSourceBusy(null);
    }
  };

  const onSaveCanned = async (): Promise<void> => {
    const title = cannedDraft.title.trim();
    const content = cannedDraft.content.trim();
    if (!title || !content || cannedBusy) return;
    setCannedBusy(true);
    try {
      if (cannedDraft.id) {
        const { cannedResponse } = await api.updateCannedResponse(cannedDraft.id, {
          title,
          content,
        });
        setCanned((list) => list.map((c) => (c.id === cannedResponse.id ? cannedResponse : c)));
        toast('Saved reply updated');
      } else {
        const { cannedResponse } = await api.createCannedResponse({ title, content });
        setCanned((list) => [...list, cannedResponse]);
        toast('Saved reply added');
      }
      setCannedDraft({ id: null, title: '', content: '' });
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not save the reply', true);
    } finally {
      setCannedBusy(false);
    }
  };

  const onDeleteCanned = async (id: string): Promise<void> => {
    if (cannedBusy) return;
    setCannedBusy(true);
    try {
      await api.deleteCannedResponse(id);
      setCanned((list) => list.filter((c) => c.id !== id));
      if (cannedDraft.id === id) setCannedDraft({ id: null, title: '', content: '' });
      toast('Saved reply removed');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not remove the reply', true);
    } finally {
      setCannedBusy(false);
    }
  };

  const onPickImage = async (
    e: ChangeEvent<HTMLInputElement>,
    apply: (dataUrl: string) => void,
    fit: 'cover' | 'contain' = 'cover',
  ): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    try {
      apply(await fileToAvatarDataUrl(file, 96, fit));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not process the image', true);
    }
  };

  const onSave = async (): Promise<void> => {
    if (!draft) return;
    setSaving(true);
    try {
      const { settings } = await api.updateSettings(draft);
      setSaved(settings);
      setDraft(settings);
      toast('Settings saved');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Failed to save settings', true);
    } finally {
      setSaving(false);
    }
  };

  const onUpgrade = async (): Promise<void> => {
    setBillingBusy(true);
    try {
      const { confirmationUrl } = await api.startBilling();
      // Top-level redirect breaks out of the embedded iframe to Shopify's
      // charge-approval screen.
      window.top
        ? (window.top.location.href = confirmationUrl)
        : (window.location.href = confirmationUrl);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Failed to start upgrade', true);
      setBillingBusy(false);
    }
  };

  const onDowngrade = async (): Promise<void> => {
    setBillingBusy(true);
    try {
      const { plan } = await api.cancelBilling();
      setConfirmDowngrade(false);
      refetchStore();
      toast(plan === 'FREE' ? 'Plan downgraded to Free' : 'Plan updated');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Failed to downgrade plan', true);
    } finally {
      setBillingBusy(false);
    }
  };

  const onFaqSaved = (faq: Faq, isNew: boolean): void => {
    setFaqs((list) => (isNew ? [...list, faq] : list.map((f) => (f.id === faq.id ? faq : f))));
    setModal({ open: false });
  };

  const onFaqDelete = async (faqId: string): Promise<void> => {
    try {
      await api.deleteFaq(faqId);
      setFaqs((list) => list.filter((f) => f.id !== faqId));
      toast('Question removed');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Failed to delete question', true);
    }
  };

  // FAQ tab: search + category filter + pagination (client-side).
  const categories = useMemo(
    () => ['All', ...Array.from(new Set(faqs.map((f) => f.category)))],
    [faqs],
  );
  const filteredFaqs = useMemo(() => {
    const q = faqSearch.trim().toLowerCase();
    return faqs.filter(
      (f) =>
        (faqCategory === 'All' || f.category === faqCategory) &&
        (!q || f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q)),
    );
  }, [faqs, faqSearch, faqCategory]);
  const totalPages = Math.max(1, Math.ceil(filteredFaqs.length / FAQ_PAGE_SIZE));
  const page = Math.min(faqPage, totalPages);
  const pageFaqs = filteredFaqs.slice((page - 1) * FAQ_PAGE_SIZE, page * FAQ_PAGE_SIZE);

  const header =
    tab === 'AI Chatbot'
      ? {
          title: 'AI Chatbot Configuration',
          sub: 'Train and customize your AI assistant to handle customer inquiries.',
        }
      : tab === 'FAQs'
        ? {
            title: 'Knowledge Base (FAQ)',
            sub: 'Manage the questions and answers your AI assistant will use to assist customers automatically during checkout and storefront browsing.',
          }
        : tab === 'Storefront'
          ? {
              title: 'Storefront Widget',
              sub: 'Customize how the chat widget looks on your storefront — title, brand colors, and icons.',
            }
          : {
            title: 'System Configuration',
            sub: 'Manage your chat experience, notifications, and AI automation.',
          };

  const faqActions = (faq: Faq): ReactNode => (
    <div className="cg-faq-actions">
      <button
        type="button"
        className="cg-icon-btn"
        aria-label={`Edit: ${faq.question}`}
        onClick={() => setModal({ open: true, faq })}
      >
        <MaterialSymbol name="edit" size={18} />
      </button>
      <button
        type="button"
        className="cg-icon-btn is-danger"
        aria-label={`Delete: ${faq.question}`}
        onClick={() => void onFaqDelete(faq.id)}
      >
        <MaterialSymbol name="delete" size={18} />
      </button>
    </div>
  );

  return (
    <main className="cg-content">
      <div className="cg-tabs">
        {TABS.map((t) => {
          const lockedTab = isFree && (t === 'AI Chatbot' || t === 'FAQs');
          return (
            <button
              key={t}
              type="button"
              className={`cg-tab${tab === t ? ' is-active' : ''}${lockedTab ? ' is-locked' : ''}`}
              title={lockedTab ? `${t} is a PRO feature` : undefined}
              onClick={() =>
                lockedTab ? toast(`${t} is a PRO feature — upgrade to unlock it`) : setTab(t)
              }
            >
              {t}
              {lockedTab ? <MaterialSymbol name="lock" size={14} /> : null}
            </button>
          );
        })}
      </div>

      <div className="cg-settings-header">
        <div>
          <h1 className="cg-page-title">{header.title}</h1>
          <p className="cg-page-sub" style={{ maxWidth: 560 }}>
            {header.sub}
          </p>
        </div>
        {tab === 'FAQs' ? (
          <div className="cg-settings-actions">
            <button
              type="button"
              className="cg-btn-pill"
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              onClick={() => setModal({ open: true, faq: null })}
            >
              <MaterialSymbol name="add" size={20} />
              Add New Question
            </button>
          </div>
        ) : (
          <div className="cg-settings-actions">
            <button
              type="button"
              className="cg-btn-outline"
              disabled={!dirty}
              onClick={() => setDraft(saved)}
            >
              Discard
            </button>
            <button
              type="button"
              className="cg-btn-pill"
              disabled={!dirty || saving}
              onClick={() => void onSave()}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {error ? <div className="cg-banner cg-banner-warning">{error}</div> : null}

      {tab === 'FAQs' ? (
        /* ── FAQs tab: full-width knowledge base manager ─────────────────── */
        <div>
          <div className="cg-faq-toolbar">
            <div className="cg-faq-search">
              <MaterialSymbol name="search" size={20} />
              <input
                type="search"
                placeholder="Filter questions by keyword..."
                aria-label="Filter questions"
                value={faqSearch}
                onChange={(e) => {
                  setFaqSearch(e.target.value);
                  setFaqPage(1);
                }}
              />
            </div>
            <div className="cg-cat-row">
              <span className="cg-cat-label">Categories:</span>
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`cg-cat-chip${faqCategory === c ? ' is-active' : ''}`}
                  onClick={() => {
                    setFaqCategory(c);
                    setFaqPage(1);
                  }}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="cg-faq-list-card">
            {pageFaqs.length === 0 ? (
              <div className="cg-faq-row" style={{ textAlign: 'center' }}>
                <p className="cg-panel-sub" style={{ margin: 0 }}>
                  {faqs.length === 0
                    ? 'No questions yet — click "Add New Question" to start your knowledge base.'
                    : 'No questions match your filter.'}
                </p>
              </div>
            ) : (
              pageFaqs.map((faq) => (
                <div key={faq.id} className="cg-faq-row">
                  <div className="cg-faq-row-top">
                    <div className="cg-faq-row-meta">
                      <CategoryChip faq={faq} />
                      <span className="cg-faq-updated">
                        Updated {relativeFromNow(faq.updatedAt)}
                      </span>
                    </div>
                    {faqActions(faq)}
                  </div>
                  <p className="cg-faq-q">{faq.question}</p>
                  <p className="cg-faq-a">{faq.answer}</p>
                </div>
              ))
            )}
            <div className="cg-faq-pagefoot">
              <span className="cg-faq-count">
                Showing {pageFaqs.length} of {filteredFaqs.length} questions
              </span>
              <div className="cg-page-btns">
                <button
                  type="button"
                  className="cg-page-btn"
                  disabled={page <= 1}
                  aria-label="Previous page"
                  onClick={() => setFaqPage(page - 1)}
                >
                  <MaterialSymbol name="chevron_left" size={16} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`cg-page-btn${n === page ? ' is-current' : ' is-ghost'}`}
                    onClick={() => setFaqPage(n)}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  className="cg-page-btn"
                  disabled={page >= totalPages}
                  aria-label="Next page"
                  onClick={() => setFaqPage(page + 1)}
                >
                  <MaterialSymbol name="chevron_right" size={16} />
                </button>
              </div>
            </div>
          </div>

          <div className="cg-hint-card">
            <div className="cg-hint-icon">
              <MaterialSymbol name="lightbulb" size={30} />
            </div>
            <h3 className="cg-hint-title">Knowledge Strategy</h3>
            <p className="cg-hint-sub">
              Add more detailed answers to reduce support tickets. Your AI assistant works best
              with clear, concise FAQ entries.
            </p>
          </div>
        </div>
      ) : (
        <div className="cg-settings-grid">
          {/* Left column */}
          <div>
            {tab === 'General' ? (
              <>
                <section className="cg-panel" style={{ marginBottom: 24 }}>
                  <div className="cg-panel-icon-head">
                    <MaterialSymbol name="workspace_premium" />
                    <h2>Plan &amp; Billing</h2>
                  </div>
                  <p className="cg-panel-sub" style={{ marginTop: 0 }}>
                    {store?.plan === 'PRO'
                      ? 'You are on the PRO plan. Downgrading stops the recurring charge and switches you to Free immediately — conversations and settings are kept.'
                      : 'You are on the Free plan. Upgrade to PRO to unlock the conversations inbox, analytics, and unlimited attachments.'}
                  </p>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    {store?.plan === 'PRO' ? (
                      confirmDowngrade ? (
                        <>
                          <button
                            type="button"
                            className="cg-btn-urgent"
                            disabled={billingBusy}
                            onClick={() => void onDowngrade()}
                          >
                            {billingBusy ? 'Downgrading…' : 'Confirm downgrade to Free'}
                          </button>
                          <button
                            type="button"
                            className="cg-btn-outline"
                            disabled={billingBusy}
                            onClick={() => setConfirmDowngrade(false)}
                          >
                            Keep PRO
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="cg-btn-outline"
                          onClick={() => setConfirmDowngrade(true)}
                        >
                          Downgrade to Free
                        </button>
                      )
                    ) : (
                      <button
                        type="button"
                        className="cg-btn-primary"
                        disabled={billingBusy || !store}
                        onClick={() => void onUpgrade()}
                      >
                        {billingBusy ? 'Redirecting…' : 'Upgrade to PRO'}
                      </button>
                    )}
                  </div>
                </section>

                <section className="cg-panel" style={{ marginBottom: 24 }}>
                  <div className="cg-panel-icon-head">
                    <MaterialSymbol name="notifications_active" />
                    <h2>Notifications</h2>
                  </div>

                  <div className="cg-setting-row">
                    <div className="cg-setting-toprow">
                      <div>
                        <div className="cg-setting-name">New Message Alerts</div>
                        <div className="cg-setting-desc">
                          Receive browser sounds for incoming chats
                        </div>
                      </div>
                      <Toggle
                        on={draft?.newMessageAlerts ?? false}
                        label="New Message Alerts"
                        onClick={() => patch({ newMessageAlerts: !draft?.newMessageAlerts })}
                      />
                    </div>
                  </div>

                  <div className="cg-setting-row">
                    <div className="cg-setting-toprow">
                      <div>
                        <div className="cg-setting-name">Reminder Notifications</div>
                        <div className="cg-setting-desc">
                          Notify me if a message remains unread
                        </div>
                      </div>
                      <Toggle
                        on={draft?.reminderNotifications ?? false}
                        label="Reminder Notifications"
                        onClick={() =>
                          patch({ reminderNotifications: !draft?.reminderNotifications })
                        }
                      />
                    </div>
                    <div
                      className={`cg-choice-row${
                        draft?.reminderNotifications ? '' : ' is-disabled'
                      }`}
                    >
                      {REMINDER_CHOICES.map((minutes) => (
                        <button
                          key={minutes}
                          type="button"
                          className={`cg-choice-chip${
                            draft?.reminderMinutes === minutes ? ' is-active' : ''
                          }`}
                          onClick={() => patch({ reminderMinutes: minutes })}
                        >
                          {minutes} min
                        </button>
                      ))}
                    </div>
                  </div>
                </section>

                <ProLocked locked={isFree} onUpgrade={() => void onUpgrade()}>
                <section className="cg-panel" style={{ marginBottom: 24 }}>
                  <div className="cg-panel-icon-head">
                    <MaterialSymbol name="schedule" />
                    <h2>Chat Behavior</h2>
                  </div>

                  <div className="cg-setting-row" style={{ marginTop: 0 }}>
                    <div className="cg-setting-toprow">
                      <div>
                        <div className="cg-setting-name">Auto-close inactive chats</div>
                        <div className="cg-setting-desc">
                          Close a chat automatically when the customer hasn't replied to your (or
                          the AI's) last message for the selected time. Chats waiting on YOUR
                          reply are never auto-closed.
                        </div>
                      </div>
                      <Toggle
                        on={draft?.autoCloseEnabled ?? false}
                        label="Auto-close inactive chats"
                        onClick={() => patch({ autoCloseEnabled: !draft?.autoCloseEnabled })}
                      />
                    </div>
                    {draft?.autoCloseEnabled ? (
                      <div className="cg-choice-row" style={{ marginTop: 12 }}>
                        {AUTO_CLOSE_CHOICES.map((hours) => (
                          <button
                            key={hours}
                            type="button"
                            className={`cg-choice-chip${
                              draft?.autoCloseHours === hours ? ' is-active' : ''
                            }`}
                            onClick={() => patch({ autoCloseHours: hours })}
                          >
                            {hours} {hours === 1 ? 'hour' : 'hours'}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </section>
                </ProLocked>

                <ProLocked locked={isFree} onUpgrade={() => void onUpgrade()}>
                <section className="cg-panel" style={{ marginBottom: 24 }}>
                  <div className="cg-panel-icon-head">
                    <MaterialSymbol name="bolt" />
                    <h2>Saved Replies</h2>
                  </div>
                  <p className="cg-panel-sub" style={{ marginTop: 0 }}>
                    Reusable snippets your team can drop into a reply from the conversation
                    composer (the ⚡ button). Great for shipping updates, return steps, and
                    other repeated answers.
                  </p>

                  <div className="cg-canned-form">
                    <input
                      className="cg-input"
                      placeholder="Label (e.g. Shipping delay)"
                      maxLength={80}
                      value={cannedDraft.title}
                      onChange={(e) => setCannedDraft((d) => ({ ...d, title: e.target.value }))}
                    />
                    <textarea
                      className="cg-textarea"
                      rows={3}
                      maxLength={4000}
                      placeholder="The reply text that gets inserted…"
                      value={cannedDraft.content}
                      onChange={(e) => setCannedDraft((d) => ({ ...d, content: e.target.value }))}
                    />
                    <div className="cg-canned-form-actions">
                      {cannedDraft.id ? (
                        <button
                          type="button"
                          className="cg-btn-outline"
                          onClick={() => setCannedDraft({ id: null, title: '', content: '' })}
                        >
                          Cancel
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="cg-connect-btn"
                        disabled={cannedBusy || !cannedDraft.title.trim() || !cannedDraft.content.trim()}
                        onClick={() => void onSaveCanned()}
                      >
                        {cannedDraft.id ? 'Update reply' : 'Add reply'}
                      </button>
                    </div>
                  </div>

                  {canned.length === 0 ? (
                    <p className="cg-panel-sub" style={{ margin: 0 }}>
                      No saved replies yet — add your first above.
                    </p>
                  ) : (
                    canned.map((c) => (
                      <div key={c.id} className="cg-canned-row">
                        <div style={{ minWidth: 0 }}>
                          <p className="cg-canned-row-title">{c.title}</p>
                          <p className="cg-canned-row-content">{c.content}</p>
                        </div>
                        <div className="cg-canned-row-actions">
                          <button
                            type="button"
                            aria-label={`Edit: ${c.title}`}
                            onClick={() =>
                              setCannedDraft({ id: c.id, title: c.title, content: c.content })
                            }
                          >
                            <MaterialSymbol name="edit" size={18} />
                          </button>
                          <button
                            type="button"
                            aria-label={`Delete: ${c.title}`}
                            disabled={cannedBusy}
                            onClick={() => void onDeleteCanned(c.id)}
                          >
                            <MaterialSymbol name="delete" size={18} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </section>
                </ProLocked>

                <ProLocked locked={isFree} onUpgrade={() => void onUpgrade()}>
                <section className="cg-panel">
                  <div className="cg-panel-icon-head">
                    <MaterialSymbol name="hub" />
                    <h2>Integrations</h2>
                  </div>
                  <div className="cg-integrations-grid">
                    <div className="cg-integration">
                      <div className="cg-int-left">
                        <div className="cg-int-icon is-email">
                          <MaterialSymbol name="mail" />
                        </div>
                        <div>
                          <div className="cg-setting-name">
                            Email
                            {draft?.emailNotifyEnabled && draft.emailNotifyAddress ? (
                              <span className="cg-int-connected">Connected</span>
                            ) : null}
                          </div>
                          <div className="cg-setting-desc">
                            {draft?.emailNotifyEnabled && draft.emailNotifyAddress
                              ? `Alerts to ${draft.emailNotifyAddress}`
                              : 'Forward chat transcripts'}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="cg-connect-btn"
                        onClick={() => setEmailOpen((open) => !open)}
                      >
                        {emailOpen
                          ? 'Close'
                          : draft?.emailNotifyEnabled && draft.emailNotifyAddress
                            ? 'Manage'
                            : 'Connect'}
                      </button>
                    </div>
                    <WhatsAppCard />
                  </div>

                  {emailOpen ? (
                    <div className="cg-email-config">
                      {emailStatus && !emailStatus.configured ? (
                        <p className="cg-email-config-hint">
                          Email sending isn't active yet — add RESEND_API_KEY to the server to
                          deliver these alerts. Your preferences below are saved either way.
                        </p>
                      ) : null}

                      <div className="cg-setting-row" style={{ marginTop: 0 }}>
                        <div className="cg-setting-toprow">
                          <div className="cg-setting-name">Enable email alerts</div>
                          <Toggle
                            on={draft?.emailNotifyEnabled ?? false}
                            label="Enable email alerts"
                            onClick={() => patch({ emailNotifyEnabled: !draft?.emailNotifyEnabled })}
                          />
                        </div>
                      </div>

                      <div className="cg-setting-row">
                        <label className="cg-field-label" htmlFor="notify-email">
                          Send alerts to
                        </label>
                        <input
                          id="notify-email"
                          className="cg-input"
                          type="email"
                          placeholder="you@yourstore.com"
                          value={draft?.emailNotifyAddress ?? ''}
                          onChange={(e) =>
                            patch({ emailNotifyAddress: e.target.value.trim().toLowerCase() })
                          }
                        />
                      </div>

                      {(
                        [
                          ['emailNotifyNewConversation', 'New conversation started'],
                          ['emailNotifyNewMessage', 'Customer replies (max 1 alert / 15 min per chat)'],
                          ['emailNotifyAiHandoff', 'AI hands a customer to the team'],
                          ['emailTranscriptOnClose', 'Transcript when a chat is closed'],
                          ['emailCustomerOnClose', 'Email the CUSTOMER their chat ID when a chat is closed (lets them resume later)'],
                        ] as const
                      ).map(([key, label]) => (
                        <div key={key} className="cg-setting-row">
                          <div className="cg-setting-toprow">
                            <div className="cg-setting-name" style={{ fontSize: '0.875rem' }}>
                              {label}
                            </div>
                            <Toggle
                              on={draft?.[key] ?? false}
                              label={label}
                              onClick={() => patch({ [key]: !draft?.[key] })}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </section>
                </ProLocked>
              </>
            ) : tab === 'Storefront' ? (
              <section className="cg-panel">
                <div className="cg-panel-icon-head">
                  <MaterialSymbol name="palette" />
                  <h2>Widget Appearance</h2>
                </div>

                <ProLocked locked={isFree} onUpgrade={() => void onUpgrade()}>
                <div className="cg-setting-row">
                  <label className="cg-field-label" htmlFor="widget-title">
                    Widget Title
                  </label>
                  <input
                    id="widget-title"
                    className="cg-input"
                    maxLength={40}
                    value={draft?.widgetTitle ?? ''}
                    onChange={(e) => patch({ widgetTitle: e.target.value })}
                    placeholder="Chat with us"
                  />
                </div>

                <div className="cg-setting-row">
                  <span className="cg-field-label">Brand Colors</span>
                  <div className="cg-color-grid">
                    <div>
                      <label className="cg-color-sublabel" htmlFor="widget-color">
                        Widget Color
                      </label>
                      <div className="cg-color-field">
                        <input
                          id="widget-color"
                          type="color"
                          value={draft?.widgetColor ?? '#4a52ff'}
                          onChange={(e) => patch({ widgetColor: e.target.value })}
                        />
                        <span>{draft?.widgetColor ?? '#4a52ff'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="cg-color-sublabel" htmlFor="widget-text-color">
                        Text &amp; Icon Color
                      </label>
                      <div className="cg-color-field">
                        <input
                          id="widget-text-color"
                          type="color"
                          value={draft?.widgetTextColor ?? '#ffffff'}
                          onChange={(e) => patch({ widgetTextColor: e.target.value })}
                        />
                        <span>{draft?.widgetTextColor ?? '#ffffff'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="cg-setting-row">
                  <span className="cg-field-label">Agent Avatar</span>
                  <div className="cg-avatar-upload">
                    <div className="cg-avatar-upload-preview">
                      {draft?.widgetAgentAvatar ? (
                        <img src={draft.widgetAgentAvatar} alt="Agent avatar" />
                      ) : (
                        <WidgetGlyph name="agent" size={22} />
                      )}
                    </div>
                    <button
                      type="button"
                      className="cg-connect-btn"
                      onClick={() => agentAvatarInput.current?.click()}
                    >
                      {draft?.widgetAgentAvatar ? 'Replace image' : 'Upload image'}
                    </button>
                    {draft?.widgetAgentAvatar ? (
                      <button
                        type="button"
                        className="cg-connect-btn"
                        onClick={() => patch({ widgetAgentAvatar: '' })}
                      >
                        Remove
                      </button>
                    ) : null}
                    <input
                      ref={agentAvatarInput}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) =>
                        void onPickImage(e, (d) => patch({ widgetAgentAvatar: d }), 'contain')
                      }
                    />
                  </div>
                  <p className="cg-field-hint">
                    Shown next to your replies in the storefront chat. Transparent PNG logos look
                    best.
                  </p>
                </div>
                </ProLocked>

                <div className="cg-setting-row">
                  <span className="cg-field-label">Launcher Icon</span>
                  <div className="cg-icon-choice-row">
                    {LAUNCHER_ICON_CHOICES.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        className={`cg-icon-choice${
                          draft?.widgetLauncherIcon === icon ? ' is-active' : ''
                        }`}
                        aria-label={`Launcher icon: ${icon}`}
                        title={icon}
                        onClick={() => patch({ widgetLauncherIcon: icon })}
                      >
                        <WidgetGlyph name={icon} size={22} />
                      </button>
                    ))}
                    <button
                      type="button"
                      className={`cg-icon-choice${
                        draft?.widgetLauncherIcon === 'custom' ? ' is-active' : ''
                      }`}
                      aria-label="Launcher icon: custom image"
                      title={isFree ? 'Custom image — PRO feature' : 'Custom image'}
                      disabled={isFree}
                      onClick={() => {
                        if (draft?.widgetLauncherImage) patch({ widgetLauncherIcon: 'custom' });
                        else launcherImageInput.current?.click();
                      }}
                    >
                      {draft?.widgetLauncherImage ? (
                        <img
                          className="cg-icon-choice-img"
                          src={draft.widgetLauncherImage}
                          alt="Custom launcher"
                        />
                      ) : (
                        <MaterialSymbol name="add_photo_alternate" size={22} />
                      )}
                    </button>
                    <input
                      ref={launcherImageInput}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) =>
                        void onPickImage(
                          e,
                          (d) => patch({ widgetLauncherImage: d, widgetLauncherIcon: 'custom' }),
                          'contain',
                        )
                      }
                    />
                  </div>
                  {!isFree && draft?.widgetLauncherImage ? (
                    <div className="cg-icon-custom-actions">
                      <button type="button" onClick={() => launcherImageInput.current?.click()}>
                        Replace image
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          patch({
                            widgetLauncherImage: '',
                            ...(draft.widgetLauncherIcon === 'custom'
                              ? { widgetLauncherIcon: 'chat' as const }
                              : {}),
                          })
                        }
                      >
                        Remove image
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="cg-setting-row">
                  <span className="cg-field-label">Send Button Icon</span>
                  <div className="cg-icon-choice-row">
                    {SEND_ICON_CHOICES.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        className={`cg-icon-choice${
                          draft?.widgetSendIcon === icon ? ' is-active' : ''
                        }`}
                        aria-label={`Send icon: ${icon}`}
                        title={icon}
                        onClick={() => patch({ widgetSendIcon: icon })}
                      >
                        <WidgetGlyph name={icon} size={20} />
                      </button>
                    ))}
                    <button
                      type="button"
                      className={`cg-icon-choice${
                        draft?.widgetSendIcon === 'custom' ? ' is-active' : ''
                      }`}
                      aria-label="Send icon: custom image"
                      title={isFree ? 'Custom image — PRO feature' : 'Custom image'}
                      disabled={isFree}
                      onClick={() => {
                        if (draft?.widgetSendImage) patch({ widgetSendIcon: 'custom' });
                        else sendImageInput.current?.click();
                      }}
                    >
                      {draft?.widgetSendImage ? (
                        <img
                          className="cg-icon-choice-img"
                          src={draft.widgetSendImage}
                          alt="Custom send button"
                        />
                      ) : (
                        <MaterialSymbol name="add_photo_alternate" size={22} />
                      )}
                    </button>
                    <input
                      ref={sendImageInput}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) =>
                        void onPickImage(
                          e,
                          (d) => patch({ widgetSendImage: d, widgetSendIcon: 'custom' }),
                          'contain',
                        )
                      }
                    />
                  </div>
                  {!isFree && draft?.widgetSendImage ? (
                    <div className="cg-icon-custom-actions">
                      <button type="button" onClick={() => sendImageInput.current?.click()}>
                        Replace image
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          patch({
                            widgetSendImage: '',
                            ...(draft.widgetSendIcon === 'custom'
                              ? { widgetSendIcon: 'send' as const }
                              : {}),
                          })
                        }
                      >
                        Remove image
                      </button>
                    </div>
                  ) : null}
                </div>
              </section>
            ) : (
              <>
                <section className="cg-panel" style={{ marginBottom: 24 }}>
                  <div
                    className="cg-panel-icon-head"
                    style={{ justifyContent: 'space-between', width: '100%' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <MaterialSymbol name="smart_toy" />
                      <h2>AI Assistant Configuration</h2>
                    </div>
                    <button
                      type="button"
                      className={`cg-active-chip${draft?.aiEnabled ? '' : ' is-off'}`}
                      onClick={() => patch({ aiEnabled: !draft?.aiEnabled })}
                      title="Toggle the AI assistant"
                    >
                      <span className="cg-status-dot" />
                      {draft?.aiEnabled ? 'Active' : 'Inactive'}
                    </button>
                  </div>

                  <div className="cg-setting-row">
                    <label className="cg-field-label" htmlFor="ai-greeting">
                      Automated Greeting
                    </label>
                    <textarea
                      id="ai-greeting"
                      className="cg-textarea"
                      rows={3}
                      maxLength={500}
                      value={draft?.aiGreeting ?? ''}
                      onChange={(e) => patch({ aiGreeting: e.target.value })}
                    />
                  </div>

                  <div className="cg-two-col" style={{ marginTop: 14 }}>
                    <div className="cg-setting-row" style={{ marginTop: 0 }}>
                      <div className="cg-setting-toprow">
                        <div className="cg-setting-name" style={{ fontSize: '0.875rem' }}>
                          Chat History Persistence
                        </div>
                        <Toggle
                          on={draft?.aiChatHistoryPersistence ?? false}
                          label="Chat History Persistence"
                          onClick={() =>
                            patch({ aiChatHistoryPersistence: !draft?.aiChatHistoryPersistence })
                          }
                        />
                      </div>
                    </div>
                    <div className="cg-setting-row" style={{ marginTop: 0 }}>
                      <div className="cg-setting-toprow">
                        <div className="cg-setting-name" style={{ fontSize: '0.875rem' }}>
                          Show "Typing…" Indicator
                        </div>
                        <Toggle
                          on={draft?.aiTypingIndicator ?? false}
                          label='Show "Typing…" Indicator'
                          onClick={() => patch({ aiTypingIndicator: !draft?.aiTypingIndicator })}
                        />
                      </div>
                    </div>
                  </div>
                </section>

                {/* FAQs ground the AI too, but they're managed once in the
                    FAQs tab — no duplicate editor here. This line keeps the
                    relationship discoverable without a second FAQ manager. */}
                <p
                  className="cg-panel-sub"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                    margin: '4px 4px 8px',
                  }}
                >
                  <MaterialSymbol name="menu_book" size={18} />
                  <span>
                    Your assistant answers from your FAQ knowledge base ({faqs.length}{' '}
                    {faqs.length === 1 ? 'entry' : 'entries'}) and the linked pages below.
                  </span>
                  <button
                    type="button"
                    onClick={() => setTab('FAQs')}
                    style={{
                      border: 0,
                      background: 'transparent',
                      color: 'var(--cg-primary)',
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    Manage FAQs →
                  </button>
                </p>

                <section className="cg-panel" style={{ marginTop: 16 }}>
                  <div className="cg-panel-icon-head">
                    <MaterialSymbol name="link" />
                    <h2>Linked Pages</h2>
                  </div>
                  <p className="cg-panel-sub" style={{ marginTop: 0 }}>
                    Link store pages (return policy, privacy policy, shipping info…) and the
                    assistant will answer from their content too. Pages re-fetch automatically
                    once a day; use Refresh after editing a page. Up to 5 pages.
                  </p>

                  <div className="cg-source-add">
                    <input
                      className="cg-input"
                      type="url"
                      placeholder="https://your-store.com/policies/refund-policy"
                      value={sourceUrl}
                      onChange={(e) => setSourceUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void onAddSource();
                      }}
                      aria-label="Page URL"
                    />
                    <button
                      type="button"
                      className="cg-connect-btn"
                      disabled={sourceBusy !== null || !sourceUrl.trim() || sources.length >= 5}
                      onClick={() => void onAddSource()}
                    >
                      {sourceBusy === 'add' ? 'Fetching…' : 'Add page'}
                    </button>
                  </div>

                  {sources.map((source) => (
                    <div key={source.id} className="cg-source-item">
                      <span
                        className={`cg-source-status is-${source.status.toLowerCase()}`}
                        title={source.status === 'ERROR' ? source.error : source.status}
                      />
                      <div className="cg-source-info">
                        <p className="cg-source-title">{source.title || source.url}</p>
                        <p className="cg-source-meta">
                          {source.url}
                          {source.fetchedAt
                            ? ` · fetched ${relativeFromNow(source.fetchedAt)}`
                            : ''}
                          {source.status === 'ERROR' ? ` · ${source.error}` : ''}
                        </p>
                      </div>
                      <div className="cg-source-actions">
                        <button
                          type="button"
                          aria-label="Refresh page content"
                          title="Refresh"
                          disabled={sourceBusy !== null}
                          onClick={() => void onRefreshSource(source.id)}
                        >
                          <MaterialSymbol
                            name={sourceBusy === source.id ? 'hourglass_empty' : 'refresh'}
                            size={18}
                          />
                        </button>
                        <button
                          type="button"
                          aria-label="Remove page"
                          title="Remove"
                          disabled={sourceBusy !== null}
                          onClick={() => void onDeleteSource(source.id)}
                        >
                          <MaterialSymbol name="delete" size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </section>
              </>
            )}
          </div>

          {/* Right column: live preview (AI assistant or storefront widget) */}
          <div className="cg-preview-col">
            {tab === 'Storefront' ? (
              <>
                <div className="cg-wpv">
                  <div className="cg-wpv-head">
                    <div>
                      <h2 className="cg-wpv-title">{draft?.widgetTitle?.trim() || 'Chat with us'}</h2>
                      <div className="cg-wpv-status">
                        <span className="cg-wpv-status-dot" />
                        Online
                      </div>
                    </div>
                    <WidgetGlyph name="close" size={18} />
                  </div>
                  <div className="cg-wpv-body">
                    <div className="cg-wpv-msgrow">
                      <div
                        className="cg-wpv-msg-avatar is-agent"
                        style={{ color: draft?.widgetColor ?? '#4a52ff' }}
                      >
                        {draft?.widgetAgentAvatar ? (
                          <img src={draft.widgetAgentAvatar} alt="" />
                        ) : (
                          <WidgetGlyph name="agent" size={13} />
                        )}
                      </div>
                      <div className="cg-wpv-bubble is-admin">Hi! How can we help you today?</div>
                    </div>
                    <div className="cg-wpv-msgrow is-customer">
                      <div
                        className="cg-wpv-bubble is-customer"
                        style={{
                          background: draft?.widgetColor ?? '#4a52ff',
                          color: draft?.widgetTextColor ?? '#ffffff',
                        }}
                      >
                        I'd like to check my order status.
                      </div>
                      <div
                        className="cg-wpv-msg-avatar"
                        style={{
                          background: draft?.widgetColor ?? '#4a52ff',
                          color: draft?.widgetTextColor ?? '#ffffff',
                        }}
                      >
                        JD
                      </div>
                    </div>
                    <span className="cg-wpv-time">Just now</span>
                  </div>
                  <div className="cg-wpv-foot">
                    <div className="cg-wpv-composer">
                      <span>Ask a question…</span>
                      <button
                        type="button"
                        className="cg-wpv-send"
                        tabIndex={-1}
                        style={{
                          background: draft?.widgetColor ?? '#4a52ff',
                          color: draft?.widgetTextColor ?? '#ffffff',
                        }}
                      >
                        {draft?.widgetSendIcon === 'custom' && draft.widgetSendImage ? (
                          <img
                            className="cg-wpv-send-img"
                            src={draft.widgetSendImage}
                            alt="Custom send button"
                          />
                        ) : (
                          <WidgetGlyph
                            name={
                              draft?.widgetSendIcon === 'custom'
                                ? 'send'
                                : (draft?.widgetSendIcon ?? 'send')
                            }
                            size={16}
                          />
                        )}
                      </button>
                    </div>
                    <p className="cg-wpv-powered">
                      Powered by <strong>ChatForge</strong>
                    </p>
                  </div>
                </div>
                <div className="cg-wpv-launcher-row">
                  <div
                    className="cg-wpv-launcher"
                    style={{
                      background: draft?.widgetColor ?? '#4a52ff',
                      color: draft?.widgetTextColor ?? '#ffffff',
                      boxShadow: `0 16px 40px ${draft?.widgetColor ?? '#4a52ff'}59`,
                    }}
                  >
                    {draft?.widgetLauncherIcon === 'custom' && draft.widgetLauncherImage ? (
                      <img
                        className="cg-wpv-launcher-img"
                        src={draft.widgetLauncherImage}
                        alt="Custom launcher"
                      />
                    ) : (
                      <WidgetGlyph
                        name={
                          draft?.widgetLauncherIcon === 'custom'
                            ? 'chat'
                            : (draft?.widgetLauncherIcon ?? 'chat')
                        }
                        size={24}
                      />
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
            <div className="cg-preview">
              <div className="cg-preview-head">
                <div className="cg-preview-head-left">
                  <div className="cg-preview-avatar">
                    <MaterialSymbol name="bolt" size={18} fill />
                  </div>
                  <div>
                    <div className="cg-preview-title">ChatForge Assistant</div>
                    <div className="cg-preview-status">
                      {draft?.aiEnabled ? 'Online & Ready' : 'Standing by'}
                    </div>
                  </div>
                </div>
                <MaterialSymbol name="more_horiz" size={20} />
              </div>
              <div className="cg-preview-body">
                <span className="cg-preview-day">Today</span>
                <div className="cg-pv-row">
                  <div className="cg-pv-bot-avatar">
                    <MaterialSymbol name="smart_toy" size={14} />
                  </div>
                  <div className="cg-pv-bubble">
                    {draft?.aiGreeting?.trim() || 'Hello! How can I help you today?'}
                  </div>
                </div>
                <div className="cg-pv-user">
                  {faqs[0]?.question ?? 'What is your return policy?'}
                </div>
                {draft?.aiTypingIndicator ? (
                  <div className="cg-pv-row">
                    <div className="cg-pv-bot-avatar">
                      <MaterialSymbol name="smart_toy" size={14} />
                    </div>
                    <div className="cg-pv-bubble" style={{ fontStyle: 'italic' }}>
                      Typing…
                    </div>
                  </div>
                ) : faqs[0] ? (
                  <div className="cg-pv-row">
                    <div className="cg-pv-bot-avatar">
                      <MaterialSymbol name="smart_toy" size={14} />
                    </div>
                    <div className="cg-pv-bubble">{faqs[0].answer}</div>
                  </div>
                ) : null}
              </div>
              <div className="cg-preview-foot">
                <div className="cg-preview-input">Write a message…</div>
                <button type="button" className="cg-preview-send" tabIndex={-1}>
                  <MaterialSymbol name="send" size={15} />
                </button>
              </div>
            </div>

            <div className="cg-usage-card">
              <div className="cg-usage-toprow">
                <span className="cg-usage-label">AI Usage</span>
                <span className="cg-usage-pct">
                  {aiStatus ? Math.min(100, Math.round((aiStatus.used / aiStatus.limit) * 100)) : 0}
                  %
                </span>
              </div>
              <div className="cg-usage-bar">
                <div
                  style={{
                    width: `${aiStatus ? Math.min(100, (aiStatus.used / aiStatus.limit) * 100) : 0}%`,
                  }}
                />
              </div>
              <p className="cg-usage-note">
                {aiStatus
                  ? `${aiStatus.used.toLocaleString()} / ${aiStatus.limit.toLocaleString()} AI replies this month.${
                      aiStatus.configured
                        ? ''
                        : ' AI replies are paused — add ANTHROPIC_API_KEY to the server to activate them.'
                    }`
                  : '— / 1,000 AI replies this month.'}
              </p>
            </div>
              </>
            )}
          </div>
        </div>
      )}

      {modal.open ? (
        <FaqModal
          faq={modal.faq}
          onClose={() => setModal({ open: false })}
          onSaved={onFaqSaved}
        />
      ) : null}

      {dirty ? (
        <div className="cg-savebar" role="status">
          <MaterialSymbol name="info" size={18} />
          <span className="cg-savebar-text">You have unsaved changes</span>
          <div className="cg-savebar-actions">
            <button type="button" className="cg-btn-outline" onClick={() => setDraft(saved)}>
              Discard
            </button>
            <button
              type="button"
              className="cg-btn-pill"
              disabled={saving}
              onClick={() => void onSave()}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      ) : null}
    </main>
  );
}
